import React from 'react';
import { MoreOutlined } from '@ant-design/icons';
import { useDraggable } from '@dnd-kit/core';
import ITreeNode from '../../tree/ITreeNode';

interface ExternalTreeNodeProps {
  node: ITreeNode;
  level: number;
  allNodes: ITreeNode[];
}

const ExternalTreeNode: React.FC<ExternalTreeNodeProps> = ({ 
  node, 
  level, 
  allNodes 
}) => {
  const [isHovered, setIsHovered] = React.useState(false); 
  // Use node.id directly without prefix to match new drag logic
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: node.id,
    data: { 
      node,
      type: 'external'
    },
  });

  // Find children
  const children = allNodes.filter(n => n.parent === node.id);
  const hasChildren = children.length > 0;

  const paddingValue = '12px 24px';
  const style: React.CSSProperties = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    cursor: isDragging ? 'grabbing' : 'grab',
    backgroundColor: isHovered && !isDragging ? '#E6F7FF' : 'transparent',
    transition: 'background-color 0.2s ease',
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      {...listeners} 
      {...attributes}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {isDragging ? (
        // When dragging, show only a placeholder since DragOverlay will render the actual content
        <div style={{ width: '100%', minHeight: '20px', opacity: 0.4 }} />
      ) : (
        <>
          <div
            className={`flex items-center gap-2 text-[14px] relative leading-[22px] text-[rgba(0,0,0,0.65)] relative transition-colors`}
            style={{
              borderBottom: '1px solid var(--colorBorder, rgba(0, 0, 0, 0.06))',
              padding: paddingValue,
            }}
          >
            {level > 0 && <MoreOutlined className="w-4 h-4 mr-1" />}
            {node.text}
          </div>

          {hasChildren && (
            <div>
              {children.map((childNode, idx) => (
                <ExternalTreeNode
                  key={childNode.id}
                  node={childNode}
                  level={level + 1}
                  allNodes={allNodes}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ExternalTreeNode;
